import {
	completeAssessmentResponse,
	createAssessment,
	createOrganizationInvite,
	deleteUserFromOrganization,
	getAssessment,
	getAssessments,
	getPaginatedAssessments,
	getFullTrajectory,
	getMeV2,
	getOrganizationInvite,
	getOrganizationInvites,
	getOrganizationRoles,
	getOrganizationUsers,
	getPosition,
	getPositions,
	getUser,
	getUserOrganizationRoles,
	getUserOrganizationRolesV2,
	getUserPositionAssessments,
	getUserV2,
	IAssessmentFilters,
	ICreateAssessmentRequest,
	ICreateOrgInviteRequest,
	IInviteAction,
	IPositionsFilters,
	ISaveAnswersRequest,
	IUserPositionAssessmentFilters,
	promoteUser,
	saveAnswers,
	updateOrganizationInvite,
	updateUserManager,
	updateUserOrganizationRoles,
} from './endpoints'
import { HttpClient, HttpClientFactory } from '../httpClient'
import { updateUser, UpdateUserDto } from './endpoints/updateUser'
import { NextPeakBearerAuthenticator } from './nextPeakAuthenticator.ts'
import { getAnswers, IGetAnswersFilter } from './endpoints/getAnswers.ts'
import { getRoles } from './endpoints/getRoles.ts'
import { getOrganizationUsersV2, IOrganizationUsersFilter } from './endpoints/getOrganizationUsers.v2.ts'
import { getOrganizationPeriodsV2 } from './endpoints/getOrganizationPeriods.v2.ts'

export interface INextpeakClientOpts {
	baseUrl: string
	getNewTokens: () => Promise<void>
	orgId: string
}

export class NextpeakClient {
	readonly client: HttpClient

	constructor(
		private readonly opts: INextpeakClientOpts,
		httpClientFactory: HttpClientFactory
	) {
		this.client = httpClientFactory.createClient(this.opts.baseUrl, {
			clientName: this.constructor.name,
			defaultAuthenticator: new NextPeakBearerAuthenticator({
				getNewTokens: this.opts.getNewTokens,
			}),
			axiosConfig: {
				headers: {
					'x-organization-Id': this.opts.orgId,
				},
			},
		})
	}

	async getOrganizationUsers(organizationId: string, managerId?: string, includes?: string[]) {
		return getOrganizationUsers(this.client, organizationId, managerId, includes)
	}

	async getOrganizationUsersV2(organizationId: string, filters?: IOrganizationUsersFilter) {
		return getOrganizationUsersV2(this.client, organizationId, filters)
	}

	async getOrganizationPeriodsV2(organizationId: string) {
		return getOrganizationPeriodsV2(this.client, organizationId)
	}

	async createOrganizationInvite(inviteData: ICreateOrgInviteRequest) {
		return createOrganizationInvite(this.client, inviteData)
	}

	async getOrganizationRoles() {
		return getOrganizationRoles(this.client)
	}

	async getOrganizationInvite(inviteId: string) {
		return getOrganizationInvite(this.client, inviteId)
	}

	async updateOrganizationInvite(inviteId: string, inviteAction: IInviteAction) {
		return updateOrganizationInvite(this.client, inviteId, inviteAction)
	}

	async getUserOrganizationRoles(userId: string) {
		return getUserOrganizationRoles(this.client, userId)
	}

	async getUserOrganizationRolesV2(userId: string) {
		return getUserOrganizationRolesV2(this.client, userId)
	}

	async getUser(userId: string) {
		return getUser(this.client, userId)
	}

	async updateUserOrganizationRoles(userId: string, roleIds: string[]) {
		return updateUserOrganizationRoles(this.client, userId, roleIds)
	}

	async promoteUser(userId: string, positionId: string) {
		return promoteUser(this.client, userId, positionId)
	}

	async updateUserManager(userId: string, managerId: string) {
		return updateUserManager(this.client, userId, managerId)
	}

	async updateUser(userId: string, updateData: UpdateUserDto) {
		return updateUser(this.client, userId, updateData)
	}

	async getFullTrajectory(path: string) {
		return getFullTrajectory(this.client, path)
	}

	async getOrganizationInvites(organizationId: string) {
		return getOrganizationInvites(this.client, organizationId)
	}

	async getPosition(positionId: string, includes?: string[]) {
		return getPosition(this.client, positionId, includes)
	}

	async getPositions(filter?: IPositionsFilters) {
		return getPositions(this.client, filter)
	}

	async getUserV2(userId: string, includes?: string[]) {
		return getUserV2(this.client, userId, includes)
	}

	async getMeV2() {
		return getMeV2(this.client)
	}

	async getPaginatedAssessments(filters?: IAssessmentFilters) {
		return getPaginatedAssessments(this.client, filters)
	}

	async getAssessments(filters?: IAssessmentFilters) {
		return getAssessments(this.client, filters)
	}

	async getAssessment(assessmentId: string, includes?: string[]) {
		return getAssessment(this.client, assessmentId, includes)
	}

	async createAssessment(assessment: ICreateAssessmentRequest) {
		return createAssessment(this.client, assessment)
	}

	async completeAssessmentResponse(assessmentId: string) {
		return completeAssessmentResponse(this.client, assessmentId)
	}

	async saveAnswers(assessmentResponseId: string, answers: ISaveAnswersRequest) {
		return saveAnswers(this.client, assessmentResponseId, answers)
	}

	async getUserPositionAssessments(userId: string, filters: IUserPositionAssessmentFilters) {
		return getUserPositionAssessments(this.client, userId, filters)
	}

	async getAnswers(filters: IGetAnswersFilter) {
		return getAnswers(this.client, filters)
	}

	async getRoles() {
		return getRoles(this.client)
	}

	async deleteUserFromOrganization(organizationId: string, userId: string) {
		return deleteUserFromOrganization(this.client, organizationId, userId)
	}
}
